import {
  Text,
  GreyGrey50,
  GreyGrey60,
  IconCircleInfo,
  IndigoIndigo60,
  Link,
  Popover,
  Primary13Regular,
  Primary13SemiBold,
  TextAreaFieldInput,
  TextFieldInput,
  Flex
} from '@sequencehq/core-components'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  CurrentInvoiceBillingPeriodPicker,
  CurrentInvoiceDatePicker,
  CurrentInvoiceDueDatePicker,
  triggeredInspectorItemIdAtom
} from 'Invoices/components/CreateEditShared'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import {
  clientInvoiceAtom,
  humanReadableDatesAtom,
  invoiceCustomerIdAtom,
  invoiceStatusAtom,
  isSavePendingAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { DATE_FORMAT_DISPLAY, DUE_DATE_PENDING_TEXT } from 'Invoices/constants'
import {
  BillingContactsPills,
  CustomerBillingContactsSelect,
  useBillingContacts
} from 'modules/CustomerContacts'
import { EditableSection } from './EditableSection'
import { SentInvoiceBillingAddress } from './SentInvoiceBillingAddress'
import { editingRowIdAtom } from './atoms'
import { Section } from '../../components'
import {
  GoCardlessIcon,
  StripeIcon,
  useGoCardlessIntegration,
  useStripeIntegration
} from 'modules/Integrations'
import { ReactNode, useCallback, useMemo } from 'react'
import { match } from 'ts-pattern'
import { localDateWithFormat } from '@sequencehq/utils'
import { useIsInvoiceEditable } from 'Invoices/hooks/useIsInvoiceEditable'
import { useResolvedInvoiceDueDate } from 'Invoices/hooks/useResolvedInvoiceDueDate'
import { usePermissions } from 'lib/rbac'
import { usePaymentProviderForInvoice } from 'Invoices/hooks/usePaymentProviderForInvoice'
import { useNonVoidedCreditNotes } from 'Invoices/hooks/useNonVoidedCreditNotes'
import { CreditNote } from 'CreditNotes/types'
import { inflect } from 'inflection'

const INPUT_WIDTH: string = '300px'

const CreditNoteLink = ({ creditNote }: { creditNote: CreditNote }) => (
  <Text
    as="span"
    {...Primary13Regular}
    color={IndigoIndigo60}
    data-testid={`summary.credit-note.${creditNote.id}`}
  >
    <Link.Unstyled to={`/credit-notes/${creditNote.id}`}>
      {creditNote.creditNoteNumber ?? 'Draft credit note'}
    </Link.Unstyled>
  </Text>
)

export const Summary = () => {
  const customerId = useAtomValue(invoiceCustomerIdAtom)
  const [clientInvoice, partialUpdateInvoice] = useAtom(clientInvoiceAtom)
  const setEditingRowId = useSetAtom(editingRowIdAtom)
  const humanReadableDates = useAtomValue(humanReadableDatesAtom)
  const isEditable = useIsInvoiceEditable()
  const invoiceStatus = useAtomValue(invoiceStatusAtom)
  const { can } = usePermissions()
  const isSentAndEditable =
    invoiceStatus === 'SENT' && can('org.invoice:update')
  const isPurchaseOrderNumberEditable = isSentAndEditable
  const isBillingAddressEditable = isSentAndEditable
  const isMemoEditable = isSentAndEditable
  const isReferenceEditable = isSentAndEditable
  const isSavePending = useAtomValue(isSavePendingAtom)
  const { enabled: stripeIntegrationEnabled } = useStripeIntegration()
  const { enabled: goCardlessIntegrationEnabled } = useGoCardlessIntegration()
  const setTriggeredInspectorItemId = useSetAtom(triggeredInspectorItemIdAtom)
  const paymentProvider = usePaymentProviderForInvoice()
  const { creditNotes } = useNonVoidedCreditNotes({
    creditNoteIds: clientInvoice?.creditNoteIds ?? []
  })
  const dueDateResolution = useResolvedInvoiceDueDate()

  const dismissFieldOnKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        ['Enter'].includes(e.key) &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.shiftKey
      ) {
        e.preventDefault()
        setEditingRowId(null)
      }
    },
    [setEditingRowId]
  )

  if (!clientInvoice || !dueDateResolution) return null

  return (
    <Section title="Summary" titleSize="lg" data-testid="invoice.summary">
      <EditableSection isEditingEnabled={isEditable}>
        <EditableSection.Row
          id="customer"
          label="Customer"
          padding="3px 8px"
          data-testid="summary.customer"
        >
          {customerId && (
            <CustomerPreviewCardPill
              customerId={customerId}
              data-testid="summary.customer.pill"
            />
          )}
        </EditableSection.Row>
        {isBillingAddressEditable && (
          <EditableSection.Row
            id="billing-address"
            label="Billing address"
            padding="4px 8px"
            isEditable
            data-testid="summary.billing-address"
          >
            {(isEditing: boolean) => (
              <SentInvoiceBillingAddress
                isEditing={isEditing}
                clientInvoice={clientInvoice}
                partialUpdateInvoice={partialUpdateInvoice}
              />
            )}
          </EditableSection.Row>
        )}
        <EditableSection.Row
          id="billing-contacts"
          label="Billing contacts"
          padding={isEditable ? '4px 8px' : undefined}
          data-testid="summary.billing-contacts"
        >
          {isEditable ? (
            <CustomerBillingContactsSelect
              customerId={customerId ?? ''}
              disabled={isSavePending}
              data-testid="summary.billing-contacts.contacts"
            />
          ) : (
            <CustomerBillingContactsList customerId={customerId ?? ''} />
          )}
        </EditableSection.Row>
        <EditableSection.Row
          id="accounting-date"
          label="Invoice date"
          data-testid="summary.invoice-date"
        >
          {isEditing =>
            isEditing ? (
              <CurrentInvoiceDatePicker
                inputWidth={INPUT_WIDTH}
                onChange={() => {
                  setEditingRowId(null)
                }}
                data-testid="summary.invoice-date.field"
                openOnMount={true}
              />
            ) : (
              <Value>
                {clientInvoice.accountingDate
                  ? localDateWithFormat(
                      clientInvoice.accountingDate,
                      DATE_FORMAT_DISPLAY
                    )
                  : ''}
              </Value>
            )
          }
        </EditableSection.Row>
        {clientInvoice.issueDate && (
          <EditableSection.Row
            id="issue-date"
            label="Issue date"
            data-testid="summary.issue-date"
          >
            <Value>
              {localDateWithFormat(
                clientInvoice.issueDate,
                DATE_FORMAT_DISPLAY
              )}
            </Value>
          </EditableSection.Row>
        )}
        <EditableSection.Row
          id="due-date"
          label={
            <Flex gap="8px">
              <EditableSection.RowLabel>Due date</EditableSection.RowLabel>
              {match(dueDateResolution)
                .with({ type: 'PENDING' }, () => (
                  <DueDateTooltip
                    content={PENDING_TOOLTIP_CONTENT}
                    data-testid="summary.due-date.pending-popover"
                  />
                ))
                .with({ type: 'RESOLVED', isMerchantDefault: true }, () => (
                  <DueDateTooltip
                    content={MERCHANT_DEFAULT_TOOLTIP_CONTENT}
                    data-testid="summary.due-date.default-popover"
                  />
                ))
                .otherwise(() => null)}
            </Flex>
          }
          data-testid="summary.due-date"
        >
          {match(dueDateResolution)
            .with({ type: 'PENDING' }, () => (
              <Value>{DUE_DATE_PENDING_TEXT}</Value>
            ))
            .with(
              { type: 'RESOLVED' },
              ({ dueDate }) =>
                (isEditing: boolean) =>
                  isEditing ? (
                    <DueDatePolicyField data-testid="summary.due-date.field" />
                  ) : (
                    <Value>
                      {localDateWithFormat(dueDate, DATE_FORMAT_DISPLAY)}
                    </Value>
                  )
            )
            .exhaustive()}
        </EditableSection.Row>
        {dueDateResolution.type === 'PENDING' && (
          <EditableSection.Row
            id="payment-terms"
            label={
              <Flex gap="8px">
                <EditableSection.RowLabel>
                  Payment terms
                </EditableSection.RowLabel>
                {dueDateResolution.isMerchantDefault && (
                  <DueDateTooltip
                    content={MERCHANT_DEFAULT_TOOLTIP_CONTENT}
                    data-testid="summary.payment-terms.default-popover"
                  />
                )}
              </Flex>
            }
            data-testid="summary.payment-terms"
          >
            {isEditing =>
              isEditing ? (
                <DueDatePolicyField data-testid="summary.payment-terms.field" />
              ) : (
                <Value>{`Net ${dueDateResolution.paymentTermDays}`}</Value>
              )
            }
          </EditableSection.Row>
        )}
        <EditableSection.Row
          id="billing-period"
          label="Billing period"
          data-testid="summary.billing-period"
        >
          {isEditing =>
            isEditing ? (
              <CurrentInvoiceBillingPeriodPicker
                inputWidth={INPUT_WIDTH}
                data-testid="summary.billing-period.field"
                openOnMount={true}
              />
            ) : (
              <Value>{humanReadableDates.billingPeriod}</Value>
            )
          }
        </EditableSection.Row>
        {stripeIntegrationEnabled && (
          <EditableSection.Row
            id="stripe-integration"
            label="Stripe"
            isEditing={false}
            isEditable={true} // Stripe link always opens inspector even if not draft
            data-testid="summary.stripe"
          >
            {() => (
              <Flex
                alignItems="center"
                gap="6px"
                cursor="pointer"
                onClick={() => {
                  setTriggeredInspectorItemId('stripe.inspector.item')
                }}
              >
                {paymentProvider === 'STRIPE' ? (
                  <>
                    <StripeIcon />
                    <Text {...Primary13Regular}>Enabled</Text>
                  </>
                ) : (
                  <Text {...Primary13Regular}>Disabled</Text>
                )}
              </Flex>
            )}
          </EditableSection.Row>
        )}
        {goCardlessIntegrationEnabled && (
          <EditableSection.Row
            id="gocardless-integration"
            label="GoCardless"
            isEditing={false}
            isEditable={true}
            data-testid="summary.gocardless"
          >
            {() => (
              <Flex
                alignItems="center"
                gap="6px"
                cursor="pointer"
                onClick={() => {
                  setTriggeredInspectorItemId('gocardless.inspector.item')
                }}
              >
                {paymentProvider === 'GOCARDLESS' ? (
                  <>
                    <GoCardlessIcon />
                    <Text {...Primary13Regular}>Enabled</Text>
                  </>
                ) : (
                  <Text {...Primary13Regular}>Disabled</Text>
                )}
              </Flex>
            )}
          </EditableSection.Row>
        )}
        {creditNotes.length > 0 && (
          <EditableSection.Row
            id="credit-note"
            label={inflect('Credit note', creditNotes.length)}
            isEditing={false}
            data-testid="summary.credit-note"
          >
            <Flex gap="8px" flexWrap="wrap">
              {creditNotes.map(creditNote => (
                <CreditNoteLink key={creditNote.id} creditNote={creditNote} />
              ))}
            </Flex>
          </EditableSection.Row>
        )}
        <EditableSection.Row
          id="memo"
          label="Memo"
          isEditable={isEditable || isMemoEditable}
          data-testid="summary.memo"
        >
          {isEditing =>
            isEditing ? (
              <TextAreaFieldInput
                autoFocus
                value={clientInvoice.memo}
                onChange={e => {
                  partialUpdateInvoice({ memo: e.target.value })
                }}
                onBlur={() => {
                  setEditingRowId(null)
                }}
                onKeyDown={dismissFieldOnKeyDown}
                data-testid="summary.memo.field"
              />
            ) : (
              <Value>{clientInvoice.memo}</Value>
            )
          }
        </EditableSection.Row>
        <EditableSection.Row
          id="po-number"
          label="PO number"
          isEditable={isEditable || isPurchaseOrderNumberEditable}
          data-testid="summary.po-number"
        >
          {isEditing =>
            isEditing ? (
              <TextFieldInput
                value={clientInvoice.purchaseOrderNumber}
                onChange={e => {
                  partialUpdateInvoice({ purchaseOrderNumber: e.target.value })
                }}
                onBlur={() => {
                  setEditingRowId(null)
                }}
                onKeyDown={dismissFieldOnKeyDown}
                autoFocus
                data-testid="summary.po-number.field"
              />
            ) : (
              <Value>{clientInvoice.purchaseOrderNumber}</Value>
            )
          }
        </EditableSection.Row>
        <EditableSection.Row
          id="reference"
          label="Reference"
          isEditable={isEditable || isReferenceEditable}
          data-testid="summary.reference"
        >
          {isEditing =>
            isEditing ? (
              <TextFieldInput
                value={clientInvoice.reference}
                onChange={e => {
                  partialUpdateInvoice({ reference: e.target.value })
                }}
                onBlur={() => {
                  setEditingRowId(null)
                }}
                onKeyDown={dismissFieldOnKeyDown}
                autoFocus
                data-testid="summary.reference.field"
              />
            ) : (
              <Value>{clientInvoice.reference}</Value>
            )
          }
        </EditableSection.Row>
      </EditableSection>
    </Section>
  )
}

const Value = ({ children }: { children: string | undefined }) => {
  if (!children)
    return (
      <Text {...Primary13Regular} color={GreyGrey60}>
        Not set
      </Text>
    )

  return (
    <Text {...Primary13Regular} whiteSpace="pre-line" wordBreak="break-word">
      {children}
    </Text>
  )
}

const CustomerBillingContactsList = ({
  customerId
}: {
  customerId: string
}) => {
  const billingContacts = useBillingContacts(customerId)

  const contacts = useMemo(
    () =>
      billingContacts.flatMap(contact =>
        contact.id && contact.email
          ? [{ id: contact.id, email: contact.email, name: contact.name }]
          : []
      ),
    [billingContacts]
  )

  if (contacts.length === 0) {
    return <Value>{undefined}</Value>
  }

  return (
    <BillingContactsPills
      contacts={contacts}
      data-testid="summary.billing-contacts.contacts"
    />
  )
}

const DueDatePolicyField = ({
  'data-testid': dataTestId
}: {
  'data-testid': string
}) => {
  const setEditingRowId = useSetAtom(editingRowIdAtom)

  return (
    <CurrentInvoiceDueDatePicker
      width={INPUT_WIDTH}
      onChange={() => {
        setEditingRowId(null)
      }}
      data-testid={dataTestId}
      initiallyOpen={true}
    />
  )
}

const MERCHANT_DEFAULT_TOOLTIP_CONTENT = (
  <Text {...Primary13Regular}>
    Due date is based on the default payment terms set in{' '}
    <Text as="span" {...Primary13SemiBold} color={IndigoIndigo60}>
      <Link.Unstyled to="/settings/invoices">invoices settings.</Link.Unstyled>
    </Text>
  </Text>
)

const PENDING_TOOLTIP_CONTENT = (
  <Text {...Primary13Regular}>
    Due date will be set when this invoice is issued.
  </Text>
)

const DueDateTooltip = ({
  content,
  'data-testid': dataTestId
}: {
  content: ReactNode
  'data-testid': string
}) => (
  <Popover
    position="left"
    content={content}
    dataTestId={`${dataTestId}.content`}
  >
    <Flex data-testid={dataTestId}>
      <IconCircleInfo color={GreyGrey50} />
    </Flex>
  </Popover>
)
